import { createDefaultGeometrySlot, createDefaultMaterialSlot, type ResolvedGraph } from '../scene/layerState.js';
import {
  createFrameNode,
  createLayerNode,
  dimAuto,
  type FrameNode,
  type Scene,
  type SceneNode,
  type SceneNodeId,
} from '../scene/sceneState.js';
import {
  type AppState,
  type ViewportTarget,
} from '../state/appState.js';
import { resolveGeometrySlotFor, resolveMaterialSlotFor } from '../state/layerGraphSelectors.js';
import {
  getOwnerSceneFor,
  getViewportTargetLabel,
  isViewportTargetValid,
} from '../state/viewportTargets.js';
import type { BuildSceneContextOptions, StandaloneGraphs } from './viewportSceneContext.js';
import { vec2Clone } from '../scene/layoutMath.js';

const ISOLATED_LAYER_ROOT_ID = 'viewport_isolated_layer_root';
const ISOLATED_LAYER_NODE_ID = 'viewport_isolated_layer_node';

export type ViewportResolvedRenderTarget =
  | {
      kind: 'scene';
      scene: Scene;
      statusLabel: string;
      ownerComponentId: string | null;
      pickTarget: ViewportTarget | null;
      sceneContextOptions?: BuildSceneContextOptions;
    }
  | {
      kind: 'standalone';
      standaloneGraphs: StandaloneGraphs;
      statusLabel: string;
    }
  | {
      kind: 'empty';
      message: string;
      statusLabel: string;
    };

export function resolveViewportRenderTarget(
  appState: AppState,
  focusTarget: ViewportTarget | null = null,
): ViewportResolvedRenderTarget {
  if (appState.viewportMode === 'scene') {
    return {
      kind: 'scene',
      scene: appState.scene,
      statusLabel: 'Scene',
      ownerComponentId: null,
      pickTarget: null,
    };
  }

  const sourceLabel = appState.viewportMode === 'focus' ? 'Focus' : 'Isolate';
  const target = appState.viewportMode === 'focus'
    ? focusTarget
    : appState.viewportIsolationTarget;

  if (!target) {
    if (appState.viewportMode === 'isolation') {
      return {
        kind: 'empty',
        message: 'No isolated target.',
        statusLabel: 'Isolate target missing',
      };
    }
    return {
      kind: 'empty',
      message: 'Select a scene item or library graph.',
      statusLabel: sourceLabel,
    };
  }

  if (!isViewportTargetValid(appState, target)) {
    return {
      kind: 'empty',
      message: `${sourceLabel} target missing`,
      statusLabel: `${sourceLabel} target missing`,
    };
  }

  const label = `${sourceLabel}: ${getViewportTargetLabel(appState, target)}`;

  if (target.type === 'scene') {
    return {
      kind: 'scene',
      scene: appState.scene,
      statusLabel: label,
      ownerComponentId: null,
      pickTarget: target,
    };
  }

  if (target.type === 'component') {
    const component = appState.componentLibrary[target.componentId];
    return {
      kind: 'scene',
      scene: component.scene,
      statusLabel: label,
      ownerComponentId: target.componentId,
      pickTarget: target,
    };
  }

  if (target.type === 'layer') {
    return {
      kind: 'scene',
      scene: createIsolatedLayerScene(appState, target),
      statusLabel: label,
      ownerComponentId: target.componentId ?? null,
      pickTarget: target,
    };
  }

  if (target.type === 'frame') {
    const isolated = createIsolatedFrameScene(appState, target);
    if (!isolated) {
      return {
        kind: 'empty',
        message: `${sourceLabel} target missing`,
        statusLabel: `${sourceLabel} target missing`,
      };
    }
    return {
      kind: 'scene',
      scene: isolated.scene,
      statusLabel: label,
      ownerComponentId: target.componentId ?? null,
      pickTarget: target,
      sceneContextOptions: isolated.sceneContextOptions,
    };
  }

  const standaloneGraphs = resolveStandaloneGraphsForIsolationTarget(appState, target);
  if (!standaloneGraphs) {
    return {
      kind: 'empty',
      message: `${sourceLabel} target missing`,
      statusLabel: `${sourceLabel} target missing`,
    };
  }
  return {
    kind: 'standalone',
    standaloneGraphs,
    statusLabel: label,
  };
}

function createIsolatedLayerScene(appState: AppState, target: Extract<ViewportTarget, { type: 'layer' }>): Scene {
  const layerDef = appState.layerLibrary[target.layerId];
  const root = createFrameNode({
    id: ISOLATED_LAYER_ROOT_ID,
    name: 'Isolated Root',
    childIds: [ISOLATED_LAYER_NODE_ID],
    position: [0, 0],
    rotation: 0,
    scale: [1, 1],
  });
  const layer = createLayerNode(target.layerId, {
    id: ISOLATED_LAYER_NODE_ID,
    name: layerDef?.name ?? 'Layer',
    fit: 'fill',
    width: dimAuto(),
    height: dimAuto(),
    position: [0, 0],
    rotation: 0,
    scale: [1, 1],
  });

  return {
    rootIds: [root.id],
    nodes: {
      [root.id]: root,
      [layer.id]: layer,
    },
  };
}

function createIsolatedFrameScene(
  appState: AppState,
  target: Extract<ViewportTarget, { type: 'frame' }>,
): { scene: Scene; sceneContextOptions?: BuildSceneContextOptions } | null {
  const ownerScene = getOwnerSceneFor(appState, target.componentId);
  if (!ownerScene) return null;
  const isolatedScene = cloneSubtreeScene(ownerScene, target.frameId);
  const root = isolatedScene.nodes[target.frameId];
  if (!root || root.kind !== 'frame') return null;

  normalizeIsolatedRoot(root);
  return { scene: isolatedScene };
}

function cloneSubtreeScene(source: Scene, rootId: SceneNodeId): Scene {
  const nodes: Scene['nodes'] = {};

  function visit(nodeId: SceneNodeId): void {
    const node = source.nodes[nodeId];
    if (!node) return;

    if (node.kind === 'frame') {
      nodes[nodeId] = {
        ...node,
        position: vec2Clone(node.position),
        scale: vec2Clone(node.scale),
        origin: vec2Clone(node.origin),
        childIds: [...node.childIds],
      };
      for (const childId of node.childIds) visit(childId);
      return;
    }

    nodes[nodeId] = {
      ...node,
      position: vec2Clone(node.position),
      scale: vec2Clone(node.scale),
      origin: vec2Clone(node.origin),
      paramOverrides: { ...node.paramOverrides },
    } as SceneNode;
  }

  visit(rootId);
  return {
    rootIds: [rootId],
    nodes,
  };
}

function normalizeIsolatedRoot(root: FrameNode): void {
  root.position = [0, 0];
  root.rotation = 0;
  root.scale = [1, 1];
  root.width = dimAuto();
  root.height = dimAuto();
  root.flexGrow = 0;
  root.flexShrink = 1;
  root.flexBasis = dimAuto();
}

function resolveStandaloneGraphsForIsolationTarget(
  appState: AppState,
  target: Extract<ViewportTarget, { type: 'geometry-library' | 'material-library' }>,
): StandaloneGraphs | null {
  if (target.type === 'geometry-library') {
    const geometry = appState.geometryLibrary.builtIn[target.libraryItemId] ?? appState.geometryLibrary.user[target.libraryItemId];
    const material = resolveMaterialSlotFor(appState, createDefaultMaterialSlot());
    if (!geometry || !material) return null;
    return {
      geometry: graphFromLibraryEntry(geometry),
      material,
      graphScopeId: `library:geometry:${target.libraryItemId}`,
    };
  }

  const geometry = resolveGeometrySlotFor(appState, createDefaultGeometrySlot());
  const material = appState.materialLibrary.builtIn[target.libraryItemId] ?? appState.materialLibrary.user[target.libraryItemId];
  if (!geometry || !material) return null;
  return {
    geometry,
    material: graphFromLibraryEntry(material),
    graphScopeId: `library:material:${target.libraryItemId}`,
  };
}

function graphFromLibraryEntry(entry: ResolvedGraph): ResolvedGraph {
  return {
    nodes: entry.nodes,
    edges: entry.edges,
    sizeMode: entry.sizeMode,
  };
}
